// ============================================================
// GridCanvas — 統合 Canvas（ヘッダー + 左ガター + グリッド本体）
//
// 1つの Canvas でビューポートサイズを描画。
// 4ゾーン: 固定コーナー / ヘッダー(横連動) / 左ガター(縦連動) / 本体(両方向)
// ============================================================

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Vehicle, Reservation, StatusItem, VehicleType, GRID_CONFIG } from './types';
import {
  TIME_SLOTS,
  SLOTS_PER_DAY,
  DAY_HEIGHT_PX,
  dateToAbsoluteY,
  vehicleIndexToX,
  getBodyCanvasSize,
  toStartOfDay,
  diffInDays,
} from './useGridGeometry';
import { isDirectBookingCode } from '../../lib/reservationHelpers';

export interface GhostPosition {
  vehicleIndex: number;
  absoluteY: number;
  blockHeight: number;
}

interface Props {
  vehicles: Vehicle[];
  reservations: Reservation[];
  rangeStart: Date;
  dayCount: number;
  vehicleStatusList: StatusItem[];
  reservationStatusList: StatusItem[];
  vehicleTypes?: VehicleType[];
  todayScrollRef?: React.MutableRefObject<(() => void) | null>;
  containerRef?: React.RefObject<HTMLDivElement>;
  highlightedReservationId?: string | null;
  interactionMode?: 'none' | 'move' | 'move-free' | 'swap' | 'vehicle-swap';
  selectedReservationId?: string | null;
  ghostPosition?: GhostPosition | null;
  dragGhost?: GhostPosition | null;
  resizeGhost?: GhostPosition | null;
  zoom?: number;
  /** vehicle-swap モードで選択済みの車両ID */
  selectedVehicleId?: string | null;
  /** hover 中の予約ID（Canvas 内で追跡） */
  hoveredReservationId?: string | null;
  onHoverReservationChange?: (id: string | null) => void;
  /** hover 中の車両ヘッダーインデックス */
  hoveredHeaderIndex?: number | null;
  onHoverHeaderChange?: (index: number | null) => void;
  /** 新規予約編集中のゴースト */
  editingGhost?: GhostPosition | null;
  /** ヘッダー DnD: ドラッグ中の列インデックス */
  headerDragIndex?: number | null;
  /** ヘッダー DnD: ドロップ先の位置（列の境界） */
  headerDropTarget?: number | null;
  /** 読み込み済み期間リスト */
  loadedRanges?: { start: Date; end: Date }[];
  /** ファインダーでマッチした車両ヘッダーのインデックス */
  highlightedVehicleIndex?: number | null;
  /** 車両選択モードで選択中の車両IDセット */
  selectedVehicleIds?: Set<string> | null;
  /** 車両選択モードON/OFF */
  vehicleSelectMode?: boolean;
  /** ステージング中（未適用）の予約IDセット — 破線アンバー枠でハイライト */
  stagedReservationIds?: Set<string> | null;
}

const {
  columnWidth,
  slotHeight,
  leftGutterWidth,
  headerHeight,
  overlayMarginX,
  overlayMinHeight,
} = GRID_CONFIG;

export const DISPLAY_STATUSES = new Set([
  'booked', 'done', 'in_use', 'mail_ready', 'received', 'pending', 'offer', 'request',
]);

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土'];

// ---------- ヘルパー関数 ----------

function resolveReservationColor(r: Reservation, vehicle: Vehicle | undefined, vtColorMap: Map<string, { bg: string; text: string }>): { bg: string; text: string } {
  // 1. 予約個別の色指定
  if (r.bg_color) return { bg: r.bg_color, text: r.text_color ?? '#fff' };
  // 2. 予約の車両タイプが持つ色（car_type コード or car_type_id で検索）
  const carType = (r as any).car_type || r.car_type_id;
  if (carType) {
    const vtColor = vtColorMap.get(String(carType));
    if (vtColor) return vtColor;
  }
  // 3. アサイン車両の車種色
  if (vehicle?.vehicle_type?.bg_color) {
    return { bg: vehicle.vehicle_type.bg_color, text: vehicle.vehicle_type.text_color ?? '#fff' };
  }
  return { bg: '#3b82f6', text: '#fff' };
}

function resolveVehicleColor(v: Vehicle): { bg: string; text: string } {
  if (v.bg_color) return { bg: v.bg_color, text: v.text_color ?? '#fff' };
  if (v.vehicle_type?.bg_color) {
    return { bg: v.vehicle_type.bg_color, text: v.vehicle_type.text_color ?? '#fff' };
  }
  return { bg: '#475569', text: '#fff' };
}

function formatDateShort(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 等幅数字フォント */
const MONO_FONT = (size: number) => `${size}px "SF Mono", "Menlo", "Consolas", monospace`;

function drawRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawStar(ctx: CanvasRenderingContext2D, cx: number, cy: number, spikes: number, outerR: number, innerR: number) {
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = (Math.PI / spikes) * i - Math.PI / 2;
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function lookupStatus(statusList: StatusItem[], statusValue: string): StatusItem | undefined {
  return statusList.find(s => String(s.id) === String(statusValue) || s.code === statusValue);
}

// ---------- コンポーネント ----------

export default function GridCanvas({
  vehicles,
  reservations,
  rangeStart,
  dayCount,
  vehicleStatusList,
  reservationStatusList,
  vehicleTypes = [],
  todayScrollRef,
  containerRef: externalContainerRef,
  highlightedReservationId,
  interactionMode,
  selectedReservationId,
  ghostPosition,
  dragGhost,
  resizeGhost,
  zoom = 1,
  selectedVehicleId,
  hoveredReservationId,
  onHoverReservationChange,
  hoveredHeaderIndex,
  onHoverHeaderChange,
  editingGhost,
  headerDragIndex,
  headerDropTarget,
  loadedRanges = [],
  highlightedVehicleIndex,
  selectedVehicleIds,
  vehicleSelectMode,
  stagedReservationIds,
}: Props) {
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = externalContainerRef ?? internalContainerRef;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const hoveredIdRef = useRef<string | null>(null);
  const hoveredHeaderRef = useRef<number | null>(null);
  const cursorViewYRef = useRef<number | null>(null);

  const { width: bodyWidth, height: bodyHeight } = getBodyCanvasSize(vehicles.length, dayCount);
  const fullWidth = leftGutterWidth + bodyWidth;
  const fullHeight = headerHeight + bodyHeight;

  // 予約を車両IDでインデックス化（draw() 内で全件ループしないように）
  const reservationsByVehicle = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    for (const r of reservations) {
      const vid = String(r.vehicle_id);
      if (!map.has(vid)) map.set(vid, []);
      map.get(vid)!.push(r);
    }
    return map;
  }, [reservations]);

  // 車種の色マップ（ID + コード両方をキーにして検索可能にする）
  const vehicleTypeColorMap = useMemo(() => {
    const map = new Map<string, { bg: string; text: string }>();
    for (const vt of vehicleTypes) {
      if (!vt.bg_color) continue;
      const color = { bg: vt.bg_color, text: vt.text_color || '#fff' };
      map.set(String(vt.id), color);
      // vehicle_type_code / code でもマッチ
      if ((vt as any).vehicle_type_code) map.set(String((vt as any).vehicle_type_code), color);
      if ((vt as any).code) map.set(String((vt as any).code), color);
      if (vt.name) map.set(vt.name, color);
    }
    return map;
  }, [vehicleTypes]);

  const draw = useCallback(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const viewW = container.clientWidth;
    const viewH = container.clientHeight;
    const scrollX = container.scrollLeft;
    const scrollY = container.scrollTop;

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== viewW * dpr || canvas.height !== viewH * dpr) {
      canvas.width = viewW * dpr;
      canvas.height = viewH * dpr;
      canvas.style.width = `${viewW}px`;
      canvas.style.height = `${viewH}px`;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr * zoom, 0, 0, dpr * zoom, 0, 0);

    // zoom 補正: ビューポートを zoom で割ることで、描画空間上のサイズに変換
    const zViewW = viewW / zoom;
    const zViewH = viewH / zoom;

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, zViewW, zViewH);

    const today = toStartOfDay(new Date());

    // スクロール位置も zoom で割って描画空間に変換
    const zScrollX = scrollX / zoom;
    const zScrollY = scrollY / zoom;
    const bodyScrollX = Math.max(0, zScrollX - leftGutterWidth);
    const bodyScrollY = Math.max(0, zScrollY - headerHeight);
    const bodyViewW = zViewW - leftGutterWidth;
    const bodyViewH = zViewH - headerHeight;

    const firstDay = Math.max(0, Math.floor(bodyScrollY / DAY_HEIGHT_PX) - 1);
    const lastDay = Math.min(dayCount, Math.ceil((bodyScrollY + bodyViewH) / DAY_HEIGHT_PX) + 1);
    const firstCol = Math.max(0, Math.floor(bodyScrollX / columnWidth) - 1);
    const lastCol = Math.min(vehicles.length, Math.ceil((bodyScrollX + bodyViewW) / columnWidth) + 1);

    // ===== 1. 本体グリッド =====
    ctx.save();
    ctx.beginPath();
    ctx.rect(leftGutterWidth, headerHeight, bodyViewW, bodyViewH);
    ctx.clip();

    for (let d = firstDay; d < lastDay; d++) {
      const dayDate = new Date(rangeStart.getTime() + d * 86400000);
      const isToday = toStartOfDay(dayDate).getTime() === today.getTime();
      const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
      const dayY = headerHeight + d * DAY_HEIGHT_PX - bodyScrollY;

      if (isToday) {
        ctx.fillStyle = 'rgba(59,130,246,0.08)';
        ctx.fillRect(leftGutterWidth, dayY, bodyViewW, DAY_HEIGHT_PX);
      } else if (isWeekend) {
        ctx.fillStyle = 'rgba(255,255,255,0.02)';
        ctx.fillRect(leftGutterWidth, dayY, bodyViewW, DAY_HEIGHT_PX);
      }

      ctx.strokeStyle = isToday ? '#3b82f6' : '#475569';
      ctx.lineWidth = isToday ? 1.5 : 1;
      ctx.beginPath();
      ctx.moveTo(leftGutterWidth, dayY);
      ctx.lineTo(zViewW, dayY);
      ctx.stroke();

      TIME_SLOTS.forEach((_, si) => {
        if (si === 0) return;
        const slotY = dayY + si * slotHeight;
        if (slotY < headerHeight - slotHeight || slotY > zViewH) return;
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(leftGutterWidth, slotY);
        ctx.lineTo(zViewW, slotY);
        ctx.stroke();
      });
    }

    // 車両列縦線
    for (let vi = firstCol; vi <= lastCol; vi++) {
      const x = leftGutterWidth + vi * columnWidth - bodyScrollX;
      if (x < leftGutterWidth - 1 || x > zViewW) continue;
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, headerHeight);
      ctx.lineTo(x, zViewH);
      ctx.stroke();
    }

    // 非アクティブ車両の表示
    for (let vi = firstCol; vi <= lastCol && vi < vehicles.length; vi++) {
      const v = vehicles[vi];
      const x = leftGutterWidth + vi * columnWidth - bodyScrollX;

      if (v.out_of_use) {
        // 在庫（アーカイブ）— グレー背景 + ドットパターン
        ctx.fillStyle = 'rgba(100,116,139,0.35)';
        ctx.fillRect(x, headerHeight, columnWidth, bodyViewH);
        ctx.fillStyle = 'rgba(148,163,184,0.4)';
        for (let dy = headerHeight; dy < zViewH; dy += 10) {
          for (let dx = x + 3; dx < x + columnWidth; dx += 10) {
            ctx.fillRect(dx, dy, 2, 2);
          }
        }
      } else if (v.is_active === false) {
        // 一時利用外 — 黄色背景 + 斜線パターン
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, headerHeight, columnWidth, bodyViewH);
        ctx.clip();
        ctx.fillStyle = 'rgba(234,179,8,0.18)';
        ctx.fillRect(x, headerHeight, columnWidth, bodyViewH);
        ctx.strokeStyle = 'rgba(234,179,8,0.45)';
        ctx.lineWidth = 1;
        const step = 8;
        for (let dy = headerHeight - columnWidth; dy < zViewH + columnWidth; dy += step) {
          ctx.beginPath();
          ctx.moveTo(x, dy);
          ctx.lineTo(x + columnWidth, dy + columnWidth);
          ctx.stroke();
        }
        ctx.restore();
      } else if (v.active_from || v.active_to) {
        // 利用期間外の領域 — 青紫のハッチング（領域ごとにクリップ）
        const rangeEnd = new Date(rangeStart.getTime() + dayCount * 86400000);
        const activeFrom = v.active_from ? new Date(v.active_from) : null;
        const activeTo = v.active_to ? new Date(v.active_to) : null;
        const step = 10;

        // active_from より前の領域
        if (activeFrom && activeFrom > rangeStart) {
          const beforeY = Math.min(zViewH, headerHeight + dateToAbsoluteY(rangeStart, activeFrom) - bodyScrollY);
          const regionTop = headerHeight;
          const regionH = beforeY - regionTop;
          if (regionH > 0) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(x, regionTop, columnWidth, regionH);
            ctx.clip();
            ctx.fillStyle = 'rgba(99,102,241,0.2)';
            ctx.fillRect(x, regionTop, columnWidth, regionH);
            ctx.strokeStyle = 'rgba(99,102,241,0.4)';
            ctx.lineWidth = 1;
            for (let dy = regionTop - columnWidth; dy < regionTop + regionH; dy += step) {
              ctx.beginPath();
              ctx.moveTo(x + columnWidth, dy);
              ctx.lineTo(x, dy + columnWidth);
              ctx.stroke();
            }
            ctx.restore();
          }
        }

        // active_to より後の領域
        if (activeTo && activeTo < rangeEnd) {
          const afterY = Math.max(headerHeight, headerHeight + dateToAbsoluteY(rangeStart, activeTo) - bodyScrollY);
          const regionH = zViewH - afterY;
          if (regionH > 0) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(x, afterY, columnWidth, regionH);
            ctx.clip();
            ctx.fillStyle = 'rgba(99,102,241,0.2)';
            ctx.fillRect(x, afterY, columnWidth, regionH);
            ctx.strokeStyle = 'rgba(99,102,241,0.4)';
            ctx.lineWidth = 1;
            for (let dy = afterY - columnWidth; dy < afterY + regionH; dy += step) {
              ctx.beginPath();
              ctx.moveTo(x + columnWidth, dy);
              ctx.lineTo(x, dy + columnWidth);
              ctx.stroke();
            }
            ctx.restore();
          }
        }
      }
    }

    // ===== 未読み込み期間の不活性表示 =====
    if (loadedRanges.length > 0) {
      // loadedRanges の結合範囲を計算
      let loadedMin = loadedRanges[0].start.getTime();
      let loadedMax = loadedRanges[0].end.getTime();
      for (const lr of loadedRanges) {
        if (lr.start.getTime() < loadedMin) loadedMin = lr.start.getTime();
        if (lr.end.getTime() > loadedMax) loadedMax = lr.end.getTime();
      }
      const displayEnd = new Date(rangeStart.getTime() + dayCount * 86400000);

      // rangeStart 〜 loadedMin が未読み込み
      if (loadedMin > rangeStart.getTime()) {
        const unloadedEndY = headerHeight + dateToAbsoluteY(rangeStart, new Date(loadedMin)) - bodyScrollY;
        const regionTop = headerHeight;
        const regionH = Math.min(unloadedEndY, zViewH) - regionTop;
        if (regionH > 0) {
          ctx.fillStyle = 'rgba(15,23,42,0.6)';
          ctx.fillRect(leftGutterWidth, regionTop, bodyViewW, regionH);
          // ドットパターン
          ctx.fillStyle = 'rgba(100,116,139,0.3)';
          for (let dy = regionTop; dy < regionTop + regionH; dy += 12) {
            for (let dx = leftGutterWidth; dx < zViewW; dx += 12) {
              ctx.fillRect(dx + 4, dy + 4, 2, 2);
            }
          }
          // 「データ未読み込み」テキスト
          const centerY = (regionTop + Math.min(unloadedEndY, zViewH)) / 2;
          if (centerY > headerHeight + 20) {
            ctx.fillStyle = '#64748b';
            ctx.font = 'bold 11px system-ui, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('データ読み込み中にスクロールしてください', (leftGutterWidth + zViewW) / 2, centerY);
          }
        }
      }

      // loadedMax 〜 displayEnd が未読み込み
      if (loadedMax < displayEnd.getTime()) {
        const unloadedStartY = headerHeight + dateToAbsoluteY(rangeStart, new Date(loadedMax)) - bodyScrollY;
        const regionTop = Math.max(headerHeight, unloadedStartY);
        const regionH = zViewH - regionTop;
        if (regionH > 0) {
          ctx.fillStyle = 'rgba(15,23,42,0.6)';
          ctx.fillRect(leftGutterWidth, regionTop, bodyViewW, regionH);
          ctx.fillStyle = 'rgba(100,116,139,0.3)';
          for (let dy = regionTop; dy < regionTop + regionH; dy += 12) {
            for (let dx = leftGutterWidth; dx < zViewW; dx += 12) {
              ctx.fillRect(dx + 4, dy + 4, 2, 2);
            }
          }
        }
      }
    }

    // ===== 予約オーバーレイ =====
    const vehicleMap = new Map(vehicles.map((v) => [String(v.id), v]));
    const rangeEnd = new Date(rangeStart.getTime() + dayCount * 86400000);

    // 表示中の車両列の予約だけ描画（reservationsByVehicle インデックスを活用）
    for (let vi = firstCol - 1; vi <= lastCol + 1; vi++) {
      if (vi < 0 || vi >= vehicles.length) continue;
      const vehicle = vehicles[vi];
      const vid = String(vehicle.id);
      const vehicleReservations = reservationsByVehicle.get(vid);
      if (!vehicleReservations) continue;

      // カスケードレイアウト計算（Google Calendar 方式）
      // 後の予約が右にオフセットされて上に重なる
      const OVERLAP_INDENT = Math.max(12, columnWidth * 0.18); // 各レベルの右インデント
      interface LayoutEntry { r: Reservation; absTop: number; absBottom: number; blockHeight: number; depth: number; }
      const entries: LayoutEntry[] = [];
      for (const r of vehicleReservations) {
        if (!DISPLAY_STATUSES.has(r.status)) continue;
        const startDate = new Date(r.start_date);
        const endDate = new Date(r.end_date);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) continue;
        if (endDate <= rangeStart || startDate >= rangeEnd) continue;
        const clampedStart = startDate < rangeStart ? rangeStart : startDate;
        const clampedEnd = endDate > rangeEnd ? rangeEnd : endDate;
        const absTop = dateToAbsoluteY(rangeStart, clampedStart);
        const absBottom = dateToAbsoluteY(rangeStart, clampedEnd);
        const blockHeight = Math.max(overlayMinHeight, absBottom - absTop);
        entries.push({ r, absTop, absBottom: absTop + blockHeight, blockHeight, depth: 0 });
      }

      // 開始時刻順にソートし、カスケード深度を割り当て
      entries.sort((a, b) => a.absTop - b.absTop || b.blockHeight - a.blockHeight);
      // active[depth] = そのレベルで最後に配置された予約の absBottom
      const active: number[] = [];
      for (const e of entries) {
        // 重ならない最浅のレベルを探す
        let placed = false;
        for (let d = 0; d < active.length; d++) {
          if (e.absTop >= active[d]) {
            e.depth = d;
            active[d] = e.absBottom;
            placed = true;
            break;
          }
        }
        if (!placed) {
          e.depth = active.length;
          active.push(e.absBottom);
        }
      }

      // 描画順: depth 昇順（浅い=先の予約を先に描画、深い=後の予約が上に重なる）
      entries.sort((a, b) => a.depth - b.depth || a.absTop - b.absTop);

      for (const { r, absTop, blockHeight, depth } of entries) {
        const startDate = new Date(r.start_date);
        const endDate = new Date(r.end_date);
        if (absTop + blockHeight < bodyScrollY || absTop > bodyScrollY + bodyViewH) continue;

        const top = headerHeight + absTop - bodyScrollY;
        const cellX = leftGutterWidth + vi * columnWidth + overlayMarginX - bodyScrollX;
        const cellW = columnWidth - overlayMarginX * 2;
        const indent = depth * OVERLAP_INDENT;
        const x = cellX + indent;
        const w = Math.max(20, cellW - indent);
        const vehicle = vehicleMap.get(vid);
        const { bg, text } = resolveReservationColor(r, vehicle, vehicleTypeColorMap);

        const radius = Math.min(8, blockHeight / 2);
        const isSelected = r.id === selectedReservationId;
        const isHighlighted = r.id === highlightedReservationId;
        const isHovered = String(r.id) === hoveredReservationId;

        // ステータス別の背景描画
        const isInUse = r.status === 'in_use' || String(r.status) === '3';
        const isDone = r.status === 'done' || String(r.status) === '4';
        const statusLookup = lookupStatus(reservationStatusList, r.status);
        const isInUseResolved = statusLookup?.code === 'in_use';
        const isDoneResolved = statusLookup?.code === 'done';
        const needsOpaque = isInUse || isDone || isInUseResolved || isDoneResolved;

        ctx.globalAlpha = needsOpaque ? 0.8 : 1;
        ctx.fillStyle = bg;
        drawRoundRect(ctx, x, top, w, blockHeight, radius);
        ctx.fill();
        ctx.globalAlpha = 1;

        // 常に枠線を描画（重なり時の境界を明確にする）
        if (isInUse || isInUseResolved) {
          // 利用中: ピンク枠
          ctx.strokeStyle = '#ff25fb';
          ctx.lineWidth = 2;
        } else {
          ctx.strokeStyle = 'rgba(0,0,0,0.25)';
          ctx.lineWidth = 1;
        }
        drawRoundRect(ctx, x, top, w, blockHeight, radius);
        ctx.stroke();

        // 選択・ハイライト枠線（上書き）
        if (isSelected) {
          ctx.save();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2.5;
          drawRoundRect(ctx, x, top, w, blockHeight, radius);
          ctx.stroke();
          ctx.restore();
        } else if (isHighlighted) {
          ctx.save();
          ctx.shadowColor = '#fbbf24';
          ctx.shadowBlur = 12;
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 2;
          drawRoundRect(ctx, x, top, w, blockHeight, radius);
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.restore();
        } else if (isHovered && !isSelected) {
          ctx.save();
          ctx.shadowColor = 'rgba(255,255,255,0.9)';
          ctx.shadowBlur = 14;
          ctx.strokeStyle = 'rgba(255,255,255,0.8)';
          ctx.lineWidth = 2;
          drawRoundRect(ctx, x, top, w, blockHeight, radius);
          ctx.stroke();
          ctx.shadowBlur = 0;
          // 微妙な明るい背景オーバーレイ
          ctx.fillStyle = 'rgba(255,255,255,0.08)';
          drawRoundRect(ctx, x, top, w, blockHeight, radius);
          ctx.fill();
          ctx.restore();
        }

        // ステージング中（未適用）の予約: 破線アンバー枠でハイライト
        // （選択/検索ハイライトとは独立して重ね描き）
        if (stagedReservationIds?.has(String(r.id))) {
          ctx.save();
          ctx.setLineDash([5, 3]);
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 2;
          drawRoundRect(ctx, x + 1, top + 1, w - 2, blockHeight - 2, radius);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.restore();
        }

        // 自社サイト直販予約の星アイコン(那覇 TR- / 宮古 PI-)
        if (isDirectBookingCode(r.contact_code) && blockHeight > 14) {
          ctx.save();
          const starSize = 10;
          const starX = x + w - 4 - starSize / 2;
          const starY = top + 4 + starSize / 2;
          ctx.fillStyle = '#fef08a';
          ctx.shadowColor = 'rgba(0,0,0,0.25)';
          ctx.shadowBlur = 3;
          drawStar(ctx, starX, starY, 5, starSize / 2, starSize / 4);
          ctx.fill();
          ctx.shadowBlur = 0;
          ctx.restore();
        }

        // テキスト描画（スティッキー: ブロック上端がヘッダーに隠れてもテキストが追従）
        const blockBottom = top + blockHeight;
        // ヘッダーに潜らないように +2 のマージン
        const visibleTop = Math.max(top, headerHeight + 2);
        const visibleH = blockBottom - visibleTop;

        ctx.save();
        ctx.beginPath();
        ctx.rect(x + 2, visibleTop, w - 4, Math.max(0, visibleH));
        ctx.clip();

        ctx.fillStyle = text;
        const padding = 3;
        const textX = x + padding;
        const maxTextW = w - padding * 2 - (isDirectBookingCode(r.contact_code) ? 12 : 0);
        const LH = 13;
        // スティッキー開始位置: ブロックが見える範囲の上端から
        let textY = Math.max(top + padding + LH, visibleTop + padding + LH * 0.5);

        const textBottom = blockBottom - 2;
        const canFit = () => textY + LH < textBottom;

        // 顧客名
        if (visibleH > 10 && canFit()) {
          ctx.font = 'bold 10px system-ui, sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText(r.customer_name || '', textX, textY, maxTextW);
          textY += LH;
        }

        // 開始日時（等幅数字）
        if (visibleH > 24 && canFit()) {
          ctx.font = MONO_FONT(8);
          ctx.fillText(`S:${formatDateShort(startDate)}`, textX, textY, maxTextW);
          textY += LH;
        }

        // 終了日時（等幅数字）
        if (visibleH > 38 && canFit()) {
          ctx.font = MONO_FONT(8);
          ctx.fillText(`E:${formatDateShort(endDate)}`, textX, textY, maxTextW);
          textY += LH;
        }

        // ステータス（バッジ風：bg_color/text_color を使用）余白付き
        if (visibleH > 52 && canFit()) {
          textY += 2; // 上余白
          const status = lookupStatus(reservationStatusList, r.status);
          const statusLabel = status ? status.label : r.status;
          if (status?.bg_color) {
            const badgeFont = 'bold 8px system-ui, sans-serif';
            ctx.font = badgeFont;
            const tw = ctx.measureText(statusLabel).width;
            const bw = tw + 6;
            const bh = 13;
            const bx = textX;
            const by = textY - 9;
            ctx.fillStyle = status.bg_color;
            drawRoundRect(ctx, bx, by, bw, bh, 3);
            ctx.fill();
            ctx.fillStyle = status.text_color || '#fff';
            ctx.textBaseline = 'middle';
            ctx.fillText(statusLabel, bx + 3, by + bh / 2, maxTextW);
            ctx.textBaseline = 'alphabetic';
            ctx.fillStyle = text;
          } else {
            ctx.font = 'bold 8px system-ui, sans-serif';
            ctx.fillText(`[${statusLabel}]`, textX, textY, maxTextW);
          }
          textY += LH + 2; // 下余白
        }

        // 予約コード
        if (visibleH > 70 && canFit() && r.contact_code) {
          ctx.font = MONO_FONT(8);
          ctx.fillText(r.contact_code, textX, textY, maxTextW);
          textY += LH;
        }

        // 補償内容（CDW / NOC / 補償なし）
        if (visibleH > 84 && canFit()) {
          const hasCDW = !!(r.cdw || r.insurance_cdw);
          const hasNOC = !!(r.noc || r.insurance_noc);
          const insuranceLabel = hasCDW ? 'CDW' : hasNOC ? 'NOC' : '補償なし';
          ctx.font = 'bold 8px system-ui, sans-serif';
          ctx.fillStyle = hasCDW ? '#16a34a' : hasNOC ? '#2563eb' : '#94a3b8';
          ctx.fillText(insuranceLabel, textX, textY, maxTextW);
          ctx.fillStyle = text;
          textY += LH;
        }

        // 金額
        if (visibleH > 98 && canFit()) {
          const price = Number(r.sum_price || r.total_price || 0);
          if (price > 0) {
            ctx.font = 'bold 9px system-ui, sans-serif';
            ctx.fillText(`¥${price.toLocaleString()}`, textX, textY, maxTextW);
            textY += LH;
          }
        }

        ctx.restore();
      } // for r of vehicleReservations
    } // for vi

    // 現在時刻ライン
    const now = new Date();
    const nowY = headerHeight + dateToAbsoluteY(rangeStart, now) - bodyScrollY;
    if (nowY >= headerHeight && nowY <= zViewH) {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 1.5 / zoom;
      ctx.setLineDash([4 / zoom, 3 / zoom]);
      ctx.beginPath();
      ctx.moveTo(leftGutterWidth, nowY);
      ctx.lineTo(zViewW, nowY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(leftGutterWidth + 4, nowY, 4 / zoom, 0, Math.PI * 2);
      ctx.fill();
    }

    // ===== ゴースト描画（移動/入替 + ドラッグ作成 + 新規編集中） =====
    const ghosts: (GhostPosition | null | undefined)[] = [
      interactionMode !== 'none' ? ghostPosition : null,
      dragGhost,
      resizeGhost,
      editingGhost,
    ];
    for (const gp of ghosts) {
      if (!gp) continue;
      const gx = leftGutterWidth + gp.vehicleIndex * columnWidth + overlayMarginX - bodyScrollX;
      const gy = headerHeight + gp.absoluteY - bodyScrollY;
      const gw = columnWidth - overlayMarginX * 2;
      const gh = gp.blockHeight;

      if (gx + gw > leftGutterWidth && gx < zViewW && gy + gh > headerHeight && gy < zViewH) {
        ctx.save();
        const ghostColor = gp === editingGhost ? 'rgba(255,255,255,0.2)'
          : gp === dragGhost ? 'rgba(59,130,246,0.15)'
            : gp === resizeGhost ? 'rgba(16,185,129,0.2)'
              : 'rgba(249,115,22,0.15)';
        const ghostStroke = gp === editingGhost ? 'rgba(255,255,255,0.6)'
          : gp === dragGhost ? '#3b82f6'
            : gp === resizeGhost ? '#10b981'
              : '#f97316';
        ctx.fillStyle = ghostColor;
        drawRoundRect(ctx, gx, gy, gw, gh, 4);
        ctx.fill();
        ctx.strokeStyle = ghostStroke;
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        drawRoundRect(ctx, gx, gy, gw, gh, 4);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
    }

    ctx.restore();

    // ===== 2. 左ガター =====
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, headerHeight, leftGutterWidth, bodyViewH);
    ctx.clip();

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, headerHeight, leftGutterWidth, bodyViewH);

    for (let d = firstDay; d < lastDay; d++) {
      const dayDate = new Date(rangeStart.getTime() + d * 86400000);
      const isToday = toStartOfDay(dayDate).getTime() === today.getTime();
      const dayY = headerHeight + d * DAY_HEIGHT_PX - bodyScrollY;
      const wd = dayDate.getDay();

      if (isToday) {
        ctx.fillStyle = 'rgba(59,130,246,0.12)';
        ctx.fillRect(0, dayY, leftGutterWidth, DAY_HEIGHT_PX);
      }

      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, dayY);
      ctx.lineTo(leftGutterWidth, dayY);
      ctx.stroke();

      const dateStr = `${dayDate.getMonth() + 1}/${dayDate.getDate()}`;
      const weekdayStr = `(${WEEKDAY_JA[wd]})`;

      ctx.fillStyle = isToday ? '#60a5fa' : wd === 0 ? '#f87171' : wd === 6 ? '#60a5fa' : '#e2e8f0';
      ctx.font = 'bold 10px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(dateStr, 4, dayY + 12);

      const dateWidth = ctx.measureText(dateStr).width;
      ctx.font = '9px system-ui, sans-serif';
      ctx.fillStyle = isToday ? '#60a5fa' : wd === 0 ? '#f87171' : wd === 6 ? '#60a5fa' : '#94a3b8';
      ctx.fillText(weekdayStr, 4 + dateWidth + 2, dayY + 12);

      TIME_SLOTS.forEach((slot, si) => {
        const slotY = dayY + si * slotHeight;
        if (slotY + slotHeight < headerHeight || slotY > zViewH) return;

        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(30, slotY);
        ctx.lineTo(leftGutterWidth, slotY);
        ctx.stroke();

        if (slot.type === 'hour' || si === 0 || si === SLOTS_PER_DAY - 1) {
          ctx.fillStyle = '#94a3b8';
          ctx.font = '7px system-ui, sans-serif';
          ctx.textAlign = 'right';
          const label = slot.type === 'hour'
            ? `${String(slot.hour).padStart(2, '0')}:00`
            : slot.label;
          ctx.fillText(label, leftGutterWidth - 2, slotY + slotHeight / 2 + 2);
        }
      });
    }

    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(leftGutterWidth - 1, headerHeight);
    ctx.lineTo(leftGutterWidth - 1, zViewH);
    ctx.stroke();

    ctx.restore();

    // ===== 3. ヘッダー =====
    ctx.save();
    ctx.beginPath();
    ctx.rect(leftGutterWidth, 0, bodyViewW, headerHeight);
    ctx.clip();

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(leftGutterWidth, 0, bodyViewW, headerHeight);

    for (let vi = firstCol; vi <= lastCol && vi < vehicles.length; vi++) {
      const v = vehicles[vi];
      const x = leftGutterWidth + vi * columnWidth - bodyScrollX;
      const { bg, text } = resolveVehicleColor(v);

      ctx.fillStyle = bg;
      ctx.fillRect(x, 0, columnWidth, headerHeight);

      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + columnWidth, 0);
      ctx.lineTo(x + columnWidth, headerHeight);
      ctx.stroke();

      // 車両コード
      ctx.font = 'bold 10px system-ui, sans-serif';
      ctx.textAlign = 'center';
      const code = v.vehicle_code || v.vehicle_number || String(v.id);
      const codeY = headerHeight * 0.3;

      // json_data bg_color/text_color: 車両コードにのみ適用
      const jdBg = (v as any).json_data?.data?.bg_color;
      const jdText = (v as any).json_data?.data?.text_color;
      if (jdBg) {
        const codeW = Math.min(ctx.measureText(code).width + 8, columnWidth - 4);
        const codeH = 14;
        const codeX = x + (columnWidth - codeW) / 2;
        const codeBgY = codeY - codeH + 3;
        ctx.fillStyle = jdBg;
        drawRoundRect(ctx, codeX, codeBgY, codeW, codeH, 3);
        ctx.fill();
      }
      ctx.fillStyle = jdText || text;
      ctx.fillText(code, x + columnWidth / 2, codeY, columnWidth - 4);

      // 車種名
      if (v.vehicle_type?.name) {
        ctx.font = '8px system-ui, sans-serif';
        ctx.globalAlpha = 0.7;
        ctx.fillText(v.vehicle_type.name, x + columnWidth / 2, headerHeight * 0.52, columnWidth - 4);
        ctx.globalAlpha = 1;
      }

      // 車両ステータスバッジ
      const status = lookupStatus(vehicleStatusList, v.status);
      if (status) {
        const badgeText = status.label;
        const badgeBg = status.bg_color || '#F3F4F6';
        const badgeColor = status.text_color || '#374151';
        ctx.font = 'bold 7px system-ui, sans-serif';
        const badgeW = Math.min(ctx.measureText(badgeText).width + 8, columnWidth - 8);
        const badgeH = 12;
        const badgeX = x + (columnWidth - badgeW) / 2;
        const badgeY = headerHeight * 0.65;

        ctx.fillStyle = badgeBg;
        drawRoundRect(ctx, badgeX, badgeY, badgeW, badgeH, 4);
        ctx.fill();

        ctx.fillStyle = badgeColor;
        ctx.textAlign = 'center';
        ctx.fillText(badgeText, x + columnWidth / 2, badgeY + badgeH * 0.72, badgeW - 4);
      }

      // --- json_data カスタムフィールド表示 ---
      const jsonData = (v as any).json_data?.data;

      // ラベル三角形（左上角）
      if (jsonData?.label) {
        const triSize = 22;
        ctx.save();
        ctx.fillStyle = 'rgba(239, 68, 68, 0.85)';
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + triSize, 0);
        ctx.lineTo(x, triSize);
        ctx.closePath();
        ctx.fill();
        // ラベルテキスト（先頭3文字、三角内に斜め配置）
        const labelText = String(jsonData.label).slice(0, 3);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 7px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(labelText, x + 2, 10, triSize - 4);
        ctx.restore();
      }

      // マークドット（右上角）
      if (jsonData?.mark) {
        ctx.save();
        const dotR = 4;
        const dotX = x + columnWidth - 7;
        const dotY = 7;
        ctx.beginPath();
        ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2);
        ctx.fillStyle = jsonData.mark;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }

      // 車両選択モード: 選択済みハイライト（紫色）
      if (selectedVehicleIds?.has(String(v.id))) {
        ctx.save();
        ctx.fillStyle = 'rgba(124, 58, 237, 0.3)';
        ctx.fillRect(x, 0, columnWidth, headerHeight);
        ctx.strokeStyle = '#7c3aed';
        ctx.lineWidth = 2.5;
        ctx.strokeRect(x + 1, 1, columnWidth - 2, headerHeight - 2);
        // チェックマーク（右下）
        const ckX = x + columnWidth - 14;
        const ckY = headerHeight - 10;
        ctx.beginPath();
        ctx.arc(ckX + 4, ckY + 2, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#7c3aed';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(ckX, ckY + 2);
        ctx.lineTo(ckX + 3, ckY + 5);
        ctx.lineTo(ckX + 8, ckY - 1);
        ctx.stroke();
        ctx.restore();
      }

      // ヘッダー hover 効果
      if (hoveredHeaderIndex === vi && !(selectedVehicleId && String(v.id) === selectedVehicleId)) {
        ctx.save();
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        ctx.fillRect(x, 0, columnWidth, headerHeight);
        ctx.shadowColor = 'rgba(255,255,255,0.7)';
        ctx.shadowBlur = 10;
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x + 1, 1, columnWidth - 2, headerHeight - 2);
        ctx.shadowBlur = 0;
        ctx.restore();
      }

      // vehicle-swap 選択済みヘッダーのハイライト
      if (selectedVehicleId && String(v.id) === selectedVehicleId) {
        ctx.save();
        ctx.fillStyle = 'rgba(249,115,22,0.3)';
        ctx.fillRect(x, 0, columnWidth, headerHeight);
        ctx.strokeStyle = '#f97316';
        ctx.lineWidth = 3;
        ctx.strokeRect(x + 1, 1, columnWidth - 2, headerHeight - 2);
        ctx.restore();
      }
    }

    // vehicle-swap モード: ゴースト位置の車両ヘッダーにオレンジシャドー
    if (interactionMode === 'vehicle-swap' && ghostPosition) {
      const gvi = ghostPosition.vehicleIndex;
      if (gvi >= firstCol && gvi <= lastCol && gvi < vehicles.length) {
        const gx = leftGutterWidth + gvi * columnWidth - bodyScrollX;
        ctx.save();
        ctx.shadowColor = '#f97316';
        ctx.shadowBlur = 16;
        ctx.fillStyle = 'rgba(249,115,22,0.25)';
        ctx.fillRect(gx, 0, columnWidth, headerHeight);
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#f97316';
        ctx.lineWidth = 2;
        ctx.strokeRect(gx + 1, 1, columnWidth - 2, headerHeight - 2);
        ctx.restore();
      }
    }

    // ファインダーでマッチした車両ヘッダーのハイライト
    if (highlightedVehicleIndex != null && highlightedVehicleIndex >= firstCol && highlightedVehicleIndex <= lastCol) {
      const hx = leftGutterWidth + highlightedVehicleIndex * columnWidth - bodyScrollX;
      ctx.save();
      ctx.shadowColor = '#fbbf24';
      ctx.shadowBlur = 14;
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 2.5;
      ctx.strokeRect(hx + 1, 1, columnWidth - 2, headerHeight - 2);
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'rgba(251,191,36,0.15)';
      ctx.fillRect(hx, 0, columnWidth, headerHeight);
      ctx.restore();
    }

    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(leftGutterWidth, headerHeight - 1);
    ctx.lineTo(zViewW, headerHeight - 1);
    ctx.stroke();

    // ヘッダー DnD エフェクト
    if (headerDragIndex !== null && headerDragIndex !== undefined) {
      // ドラッグ中の列を半透明に
      const dragX = leftGutterWidth + headerDragIndex * columnWidth - bodyScrollX;
      if (dragX + columnWidth > leftGutterWidth && dragX < zViewW) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.fillRect(Math.max(leftGutterWidth, dragX), 0, columnWidth, headerHeight);
      }
    }
    if (headerDropTarget !== null && headerDropTarget !== undefined && headerDragIndex !== null && headerDragIndex !== undefined) {
      // ドロップ位置の光る線
      const dropX = leftGutterWidth + headerDropTarget * columnWidth - bodyScrollX;
      if (dropX >= leftGutterWidth - 2 && dropX <= zViewW + 2) {
        ctx.save();
        ctx.strokeStyle = '#f97316';
        ctx.lineWidth = 3;
        ctx.shadowColor = '#f97316';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(dropX, 0);
        ctx.lineTo(dropX, headerHeight);
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.restore();
      }
    }

    ctx.restore();

    // ===== 4. 固定コーナー =====
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, leftGutterWidth, headerHeight);

    const yearDate = new Date(rangeStart.getTime() + firstDay * 86400000);
    ctx.fillStyle = '#94a3b8';
    ctx.font = 'bold 11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${yearDate.getFullYear()}`, leftGutterWidth / 2, headerHeight / 2 + 4);

    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(leftGutterWidth - 1, 0);
    ctx.lineTo(leftGutterWidth - 1, headerHeight);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, headerHeight - 1);
    ctx.lineTo(leftGutterWidth, headerHeight - 1);
    ctx.stroke();

    // ===== 5. カーソル位置ルーラー線（黄色い水平線） =====
    const cursorY = cursorViewYRef.current;
    if (cursorY !== null && cursorY > headerHeight * zoom) {
      const rulerY = cursorY / zoom;
      ctx.save();
      ctx.strokeStyle = 'rgba(250, 204, 21, 0.6)';
      ctx.lineWidth = 1 / zoom;
      ctx.setLineDash([6 / zoom, 4 / zoom]);
      ctx.beginPath();
      ctx.moveTo(0, rulerY);
      ctx.lineTo(viewW / zoom, rulerY);
      ctx.stroke();
      ctx.setLineDash([]);

      // ガターに対応時刻を表示
      const scrollY = (containerRef.current?.scrollTop ?? 0) / zoom;
      const absY = rulerY - headerHeight + Math.max(0, scrollY - headerHeight);
      const dayIndex = Math.floor(absY / DAY_HEIGHT_PX);
      if (dayIndex >= 0 && dayIndex < dayCount) {
        const withinDay = absY - dayIndex * DAY_HEIGHT_PX;
        const slotIdx = Math.floor(withinDay / slotHeight);
        const slotFrac = (withinDay - slotIdx * slotHeight) / slotHeight;
        const slot = TIME_SLOTS[Math.min(slotIdx, TIME_SLOTS.length - 1)];
        let hour = 0;
        let minute = 0;
        if (slot.type === 'hour') {
          hour = slot.hour;
          minute = Math.floor(slotFrac * 60);
        } else if (slot.type === 'bucket') {
          const startH = Math.min(...slot.hours);
          const totalMin = slot.hours.length * 60;
          const m = Math.floor(slotFrac * totalMin);
          hour = startH + Math.floor(m / 60);
          minute = m % 60;
        }
        const cursorDate = new Date(rangeStart);
        cursorDate.setDate(cursorDate.getDate() + dayIndex);
        const hh = String(hour).padStart(2, '0');
        const mm = String(minute).padStart(2, '0');
        const dateTimeLabel = `  ${cursorDate.getMonth() + 1}/${cursorDate.getDate()} ${hh}:${mm}`;
        const fontSize = 9 / zoom;
        ctx.fillStyle = 'rgba(250, 204, 21, 0.9)';
        ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(dateTimeLabel, 4, rulerY - 3 / zoom);
      }
      ctx.restore();
    }

  }, [vehicles, reservationsByVehicle, rangeStart, dayCount, vehicleStatusList, reservationStatusList, vehicleTypeColorMap, bodyWidth, bodyHeight, fullWidth, fullHeight, highlightedReservationId, highlightedVehicleIndex, interactionMode, selectedReservationId, ghostPosition, dragGhost, resizeGhost, zoom, selectedVehicleId, hoveredReservationId, hoveredHeaderIndex, editingGhost, headerDragIndex, headerDropTarget, selectedVehicleIds, vehicleSelectMode, stagedReservationIds]);

  // スクロール → 再描画
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(draw);
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [draw]);

  // 初回描画 + リサイズ
  useEffect(() => {
    draw();
    const handler = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [draw]);

  // カーソル + hover 追跡: ヘッダー・予約ブロック上で pointer / hover ID 通知
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const vx = (e.clientX - rect.left) / zoom;
      const vy = (e.clientY - rect.top) / zoom;
      // ルーラー線の位置を常に更新
      const newViewY = e.clientY - rect.top;
      if (cursorViewYRef.current !== newViewY) {
        cursorViewYRef.current = newViewY;
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(draw);
      }

      if (vx > leftGutterWidth && vy < headerHeight) {
        el.style.cursor = 'pointer';
        if (hoveredIdRef.current !== null) { hoveredIdRef.current = null; onHoverReservationChange?.(null); }
        const scrollX = el.scrollLeft / zoom;
        const hBodyX = vx - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
        const hvi = Math.floor(hBodyX / columnWidth);
        if (hvi >= 0 && hvi < vehicles.length) {
          if (hoveredHeaderRef.current !== hvi) { hoveredHeaderRef.current = hvi; onHoverHeaderChange?.(hvi); }
        } else {
          if (hoveredHeaderRef.current !== null) { hoveredHeaderRef.current = null; onHoverHeaderChange?.(null); }
        }
        return;
      }
      if (hoveredHeaderRef.current !== null) { hoveredHeaderRef.current = null; onHoverHeaderChange?.(null); }
      if (vx > leftGutterWidth && vy > headerHeight) {
        const scrollX = el.scrollLeft / zoom;
        const scrollY = el.scrollTop / zoom;
        const bodyX = vx - leftGutterWidth + Math.max(0, scrollX - leftGutterWidth);
        const bodyY = vy - headerHeight + Math.max(0, scrollY - headerHeight);
        const vi = Math.floor(bodyX / columnWidth);
        if (vi >= 0 && vi < vehicles.length) {
          const vid = String(vehicles[vi].id);
          const vReservations = reservationsByVehicle.get(vid);
          if (vReservations) {
            // カスケードレイアウトの X 位置を考慮した hover 判定
            const OVERLAP_INDENT_H = Math.max(12, columnWidth * 0.18);
            const localX = bodyX - vi * columnWidth; // 列内の X 座標

            // カスケード計算（描画と同じロジック）
            interface HoverEntry { r: Reservation; absTop: number; bh: number; depth: number; }
            const hEntries: HoverEntry[] = [];
            for (const r of vReservations) {
              if (!DISPLAY_STATUSES.has(r.status) && !String(r.id).startsWith('tmp_')) continue;
              const s = new Date(r.start_date);
              const ed = new Date(r.end_date);
              if (isNaN(s.getTime()) || isNaN(ed.getTime())) continue;
              const absTop = dateToAbsoluteY(rangeStart, s < rangeStart ? rangeStart : s);
              const absBottom = dateToAbsoluteY(rangeStart, ed);
              const bh = Math.max(overlayMinHeight, absBottom - absTop);
              hEntries.push({ r, absTop, bh, depth: 0 });
            }
            hEntries.sort((a, b) => a.absTop - b.absTop || b.bh - a.bh);
            const hActive: number[] = [];
            for (const e of hEntries) {
              let placed = false;
              for (let d = 0; d < hActive.length; d++) {
                if (e.absTop >= hActive[d]) { e.depth = d; hActive[d] = e.absTop + e.bh; placed = true; break; }
              }
              if (!placed) { e.depth = hActive.length; hActive.push(e.absTop + e.bh); }
            }

            // Y + X の両方でヒットテスト（後に描画される=depth が大きいほうが視覚的に上）
            let bestId: string | null = null;
            let bestDepth = -1;
            let bestAbsTop = 0;
            let bestBh = 0;
            const RESIZE_ZONE = 10;
            for (const e of hEntries) {
              if (bodyY < e.absTop || bodyY > e.absTop + e.bh) continue;
              const indent = e.depth * OVERLAP_INDENT_H;
              const blockLeft = overlayMarginX + indent;
              const blockRight = columnWidth - overlayMarginX;
              if (localX < blockLeft || localX > blockRight) continue;
              // depth が大きい = 後に描画 = 視覚的に上 → 優先
              if (e.depth > bestDepth) {
                bestId = String(e.r.id);
                bestDepth = e.depth;
                bestAbsTop = e.absTop;
                bestBh = e.bh;
              }
            }
            if (bestId) {
              // 上下端のリサイズゾーンではカーソルを ns-resize に
              const distFromTop = bodyY - bestAbsTop;
              const distFromBottom = bestBh - distFromTop;
              el.style.cursor = (distFromTop < RESIZE_ZONE || distFromBottom < RESIZE_ZONE) ? 'ns-resize' : 'pointer';
              if (hoveredIdRef.current !== bestId) {
                hoveredIdRef.current = bestId;
                onHoverReservationChange?.(bestId);
              }
              return;
            }
          }
        }
      }
      el.style.cursor = 'default';
      if (hoveredIdRef.current !== null) { hoveredIdRef.current = null; onHoverReservationChange?.(null); }
    };
    const onLeave = () => {
      if (hoveredIdRef.current !== null) { hoveredIdRef.current = null; onHoverReservationChange?.(null); }
      if (hoveredHeaderRef.current !== null) { hoveredHeaderRef.current = null; onHoverHeaderChange?.(null); }
      if (cursorViewYRef.current !== null) { cursorViewYRef.current = null; cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(draw); }
    };
    el.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('mouseleave', onLeave);
    return () => { el.removeEventListener('mousemove', onMove); el.removeEventListener('mouseleave', onLeave); };
  }, [vehicles, reservationsByVehicle, rangeStart, zoom, onHoverReservationChange, draw]);

  // 1分ごとに現在時刻ラインを再描画
  useEffect(() => {
    const id = setInterval(draw, 60000);
    return () => clearInterval(id);
  }, [draw]);

  // 今日にスクロール
  const scrollToToday = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const todayIndex = diffInDays(rangeStart, new Date());
    if (todayIndex < 0 || todayIndex >= dayCount) return;
    const targetY = (headerHeight + todayIndex * DAY_HEIGHT_PX) * zoom;
    el.scrollTo({ top: targetY, behavior: 'smooth' });
  }, [rangeStart, dayCount, zoom]);

  useEffect(() => {
    if (todayScrollRef) todayScrollRef.current = scrollToToday;
  }, [todayScrollRef, scrollToToday]);

  // 初回マウント時に本日 0:00 の位置へ即座にスクロール
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const todayIndex = diffInDays(rangeStart, new Date());
    if (todayIndex > 0 && todayIndex < dayCount) {
      const targetY = (headerHeight + todayIndex * DAY_HEIGHT_PX) * zoom;
      el.scrollTo({ top: targetY, behavior: 'instant' });
    }
    (el as any).__initialScrollDone = true;
  }, []); // eslint-disable-line

  return (
    <div
      ref={containerRef}
      style={{ flex: 1, overflow: 'auto', background: '#0f172a', overflowAnchor: 'none' }}
    >
      <div style={{ width: fullWidth * zoom + leftGutterWidth, height: fullHeight * zoom, position: 'relative' }}>
        <canvas
          ref={canvasRef}
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            display: 'block',
          }}
        />
      </div>
    </div>
  );
}
